import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Settings, Copy, RefreshCw, AlertCircle, Globe, Radar, X, Eye, EyeOff, ShoppingBag, Receipt, Truck, Store, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_ICONS = { marketplaces: ShoppingBag, shop: Store, billing: Receipt, couriers: Truck };

// ---------- eMAG Vendor RPA (auto-reply positive reviews) ----------
function EmagVendorRPACard() {
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [replyText, setReplyText] = useState("");
  const [cookiesJson, setCookiesJson] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [runStatus, setRunStatus] = useState(null); // {status, started_at, heartbeat_at, heartbeat_age_seconds, is_stale}
  const [expanded, setExpanded] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [expiryInfo, setExpiryInfo] = useState(null);
  const [testingAlert, setTestingAlert] = useState(false);
  const [families, setFamilies] = useState([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/emag-vendor-rpa/credentials`);
      setStatus(r.data);
      setEmail(r.data.email || "");
      setReplyText(r.data.reply_text || "");
    } catch (e) { /* ignore */ }
    try {
      const r2 = await axios.get(`${API}/emag-vendor-rpa/cookies-expiry`);
      setExpiryInfo(r2.data);
    } catch (e) { /* ignore */ }
    try {
      const r3 = await axios.get(`${API}/emag-vendor-rpa/families`);
      setFamilies(r3.data.families || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  // ── Persistent status polling ─────────────────────────────────────
  // Poll the RPA state every 5 s regardless of the local `running` flag
  // so the card correctly reflects an in-flight run even after the user
  // navigated away and came back. Also drives the "stale run" warning
  // (heartbeat older than 3 min).
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await axios.get(`${API}/emag-vendor-rpa/run/status`);
        if (cancelled) return;
        const cur = s.data.current_run || null;
        setRunStatus(cur);
        setRunning(!!(cur && cur.status === "running"));
        if (s.data.last_result) setLastResult(s.data.last_result);
      } catch (e) { /* transient */ }
    };
    tick();
    const iv = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const save = async () => {
    if (!email || (!password && !status?.configured)) {
      alert("Email și parolă sunt obligatorii la prima salvare.");
      return;
    }
    setSaving(true);
    try {
      const body = { email, reply_text: replyText };
      if (password) body.password = password;
      if (cookiesJson.trim()) body.cookies_json = cookiesJson.trim();
      // If password not provided but already configured, still send the reply_text update
      if (!password && status?.configured) {
        alert("Pentru a modifica setări, reintrodu parola. E o măsură de securitate — parola nu poate fi decriptată fără reintroducere.");
        setSaving(false);
        return;
      }
      await axios.post(`${API}/emag-vendor-rpa/credentials`, body);
      setPassword("");
      setCookiesJson("");
      await load();
      alert("Setări salvate și criptate.");
    } catch (e) {
      alert("Eroare la salvare: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setLastResult(null);
    try {
      const r = await axios.post(`${API}/emag-vendor-rpa/test-connection`, {}, { timeout: 300000 }); // 5 min — first run installs Chromium (~150 MB)
      setLastResult(r.data);
      if (r.data.ok) alert("Login OK! Sessiunea funcționează. Poți rula acum răspunsurile automate.");
      else alert("Login eșuat: " + (r.data.error || "eroare necunoscută"));
    } catch (e) {
      alert("Test eșuat: " + (e.response?.data?.detail || e.message));
    } finally { setTesting(false); }
  };

  const runNow = async () => {
    const targetFam = families.find(f => f.id === selectedFamilyId);
    const familyLabel = targetFam ? `familia "${targetFam.name}" (${targetFam.unreplied_positive_count} pozitive fără reply)` : "TOATE familiile";
    if (!window.confirm(`Rulez acum răspunsurile automate LIVE pe eMAG pentru ${familyLabel}?\n\nRulare durează 2-5 min per familie (proxy rezidențial + JS render). Poți ieși din pagină — rularea continuă în background și progresul e vizibil când revii.`)) return;
    setLastResult(null);
    try {
      const body = {};
      if (selectedFamilyId) body.family_id = selectedFamilyId;
      // Fire in background — returns run_id immediately. The persistent
      // status poller (5s tick) will pick up progress and drive UI updates.
      await axios.post(`${API}/emag-vendor-rpa/run`, body, { timeout: 30000 });
      setRunning(true);
      // Refresh families list so unreplied counts drop as replies land.
      try {
        const r3 = await axios.get(`${API}/emag-vendor-rpa/families`);
        setFamilies(r3.data.families || []);
      } catch (e) { /* ignore */ }
    } catch (e) {
      alert("Rulare eșuată: " + (e.response?.data?.detail || e.message));
    }
  };

  const testExpiryAlert = async () => {
    if (!window.confirm("Trimit acum o notificare de test (clopoțel + email) pentru expirarea cookies? E doar pentru verificarea fluxului.")) return;
    setTestingAlert(true);
    try {
      const r = await axios.post(`${API}/emag-vendor-rpa/cookies-expiry/test-alert`, {});
      alert("Alertă trimisă. Verifică clopoțelul din header și inbox-ul destinatarilor din digest.\n\n" + JSON.stringify(r.data, null, 2));
    } catch (e) {
      alert("Test alertă eșuat: " + (e.response?.data?.detail || e.message));
    } finally { setTestingAlert(false); }
  };

  const sendLastResultEmail = async () => {
    try {
      const r = await axios.post(`${API}/emag-vendor-rpa/send-last-result-email`, {});
      alert(`Raport trimis pe email!\nTotal răspunsuri: ${r.data.total_replied}\nDestinatari: ${(r.data.recipients || []).map(x => x.to).join(", ")}`);
    } catch (e) {
      alert("Trimitere eșuată: " + (e.response?.data?.detail || e.message));
    }
  };

  const resetStuckRun = async () => {
    if (!window.confirm("Resetez starea unei rulări blocate? Fă asta doar dacă apare eroarea 'Deja rulează o sesiune RPA' și crezi că e blocată.")) return;
    try {
      const r = await axios.post(`${API}/emag-vendor-rpa/run/reset`, {});
      alert(`Reset OK. Poți relansa rularea.\n\nDetalii: ${JSON.stringify(r.data.previous_run || {}, null, 2)}`);
    } catch (e) {
      alert("Reset eșuat: " + (e.response?.data?.detail || e.message));
    }
  };

  const downloadLocalAgent = async () => {
    try {
      const r = await axios.get(`${API}/emag-vendor-rpa/local-agent/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "bragus_rpa_local_agent_mac.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Descărcare eșuată: " + (e.response?.data?.detail || e.message));
    }
  };

  const rebuildReviewStats = async () => {
    if (!window.confirm("Reconstruiesc statisticile recenziilor din DB (pentru cazul când pagina 'Recenzii' apare goală după un sync eșuat)?")) return;
    try {
      const r = await axios.post(`${API}/emag-vendor-rpa/rebuild-review-stats`, {});
      const lines = (r.data.results || []).map(x => `${x.family}: ${x.total} recenzii (${x.positive_pct}% pozitive)`).join("\n");
      alert(`Reconstruit ${r.data.families_updated} familii:\n\n${lines}`);
    } catch (e) {
      alert("Reconstrucție eșuată: " + (e.response?.data?.detail || e.message));
    }
  };

  const downloadDebugFile = async (endpoint, params, filename) => {
    try {
      const r = await axios.get(`${API}/emag-vendor-rpa/${endpoint}`, {
        params,
        responseType: "blob",
      });
      const blob = new Blob([r.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const detail = e.response?.data instanceof Blob
        ? await e.response.data.text().then(t => { try { return JSON.parse(t).detail; } catch { return t; } })
        : (e.response?.data?.detail || e.message);
      alert(`Descărcare eșuată: ${detail}`);
    }
  };

  return (
    <div className="mb-8 rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5" data-testid="emag-vendor-rpa-card">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-zinc-100">eMAG Vendor Auto-Reply (RPA)</h2>
          <p className="text-xs text-zinc-500">Răspunde automat cu text fix la recenziile pozitive fără reply. Rulează luni 09:00 din 10 aug.</p>
        </div>
        <div className="flex items-center gap-2">
          {status?.configured ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
              <CheckCircle2 className="h-3 w-3" /> Configurat
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-[10px] font-medium text-zinc-400">
              Neconfigurat
            </span>
          )}
          <button onClick={() => setExpanded(!expanded)} className="rounded border border-zinc-700 bg-zinc-900 px-3 py-1 text-xs text-zinc-300 hover:border-orange-500/40">
            {expanded ? "Închide" : (status?.configured ? "Editează" : "Configurează")}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-3 border-t border-zinc-800 pt-4">
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Email vendor.emag.ro</label>
            <input
              data-testid="rpa-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
              placeholder="bragusidea01@gmail.com"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-zinc-400">
              Parolă vendor.emag.ro {status?.configured && <span className="text-emerald-400">(salvată — reintrodu pentru modificare)</span>}
            </label>
            <div className="relative">
              <input
                data-testid="rpa-password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 pr-10 text-sm text-zinc-100"
                placeholder={status?.configured ? "•••••••• (reintrodu pentru schimbare)" : "parola contului"}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-2 text-zinc-500 hover:text-zinc-300">
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-1 text-[10px] text-zinc-500">Parola e criptată cu Fernet în DB. Nu e vizibilă înapoi și nu apare în log-uri.</p>
          </div>
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Textul răspunsului fix (postat la toate recenziile pozitive)</label>
            <textarea
              data-testid="rpa-reply-text-input"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={7}
              className="w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs text-zinc-100 font-mono"
            />
          </div>

          <div>
            <label className="mb-1 flex items-center justify-between text-xs text-zinc-400">
              <span>
                Cookies vendor.emag.ro (recomandat — sar peste CAPTCHA)
                {status?.cookies_configured && (
                  <span className="ml-2 rounded bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-bold text-emerald-300">
                    SALVATE {status.cookies_saved_at ? `· ${new Date(status.cookies_saved_at).toLocaleDateString("ro-RO")}` : ""}
                  </span>
                )}
                {expiryInfo?.configured && expiryInfo?.days_until_expiry != null && (
                  (() => {
                    const d = expiryInfo.days_until_expiry;
                    const expired = d <= 0;
                    const critical = d > 0 && d <= 3;
                    const cls = expired
                      ? "border-red-500/50 bg-red-500/20 text-red-200"
                      : critical
                      ? "border-amber-500/50 bg-amber-500/20 text-amber-200"
                      : "border-zinc-700 bg-zinc-800/60 text-zinc-300";
                    const label = expired
                      ? "EXPIRAT — reînnoire URGENTĂ"
                      : `Expiră în ${d.toFixed(1)} zile`;
                    return (
                      <span
                        data-testid="rpa-cookies-expiry-badge"
                        title={expiryInfo.is_estimated ? "Estimare 30 zile de la salvare (cookies session-only)" : `Cookie critic: ${expiryInfo.critical_cookie_name}`}
                        className={`ml-2 rounded border px-1.5 py-0.5 text-[9px] font-bold ${cls}`}>
                        {label}{expiryInfo.is_estimated ? " (est.)" : ""}
                      </span>
                    );
                  })()
                )}
              </span>
            </label>
            <textarea
              data-testid="rpa-cookies-input"
              value={cookiesJson}
              onChange={(e) => setCookiesJson(e.target.value)}
              rows={4}
              placeholder='Paste JSON export from "Cookie-Editor" browser extension (Export → JSON)'
              className="w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-[11px] text-zinc-100 font-mono"
            />
            <p className="mt-1 text-[10px] leading-relaxed text-zinc-500">
              1. Instalezi extensia <strong>Cookie-Editor</strong> în Chrome/Firefox.
              2. Te loghezi manual pe <code>emag.ro</code>.
              3. Click pe iconița extensiei → <code>Export</code> → <code>JSON</code>.
              4. Paste aici → Salvează. Cookies se criptează Fernet.
              5. Botul le va folosi în loc de login → skip CAPTCHA.
              6. Când sesiunea expiră (~30 zile), repeți pașii.
            </p>
            {expiryInfo?.configured && (
              <div
                data-testid="rpa-cookies-expiry-panel"
                className={`mt-2 rounded border p-2 text-[10px] ${
                  (expiryInfo.days_until_expiry ?? 999) <= 0
                    ? "border-red-500/40 bg-red-500/10 text-red-200"
                    : (expiryInfo.days_until_expiry ?? 999) <= 3
                    ? "border-amber-500/40 bg-amber-500/10 text-amber-100"
                    : "border-zinc-700 bg-zinc-900/60 text-zinc-400"
                }`}>
                <div>
                  <strong>Expirare cookies (sesiune):</strong>{" "}
                  {expiryInfo.min_expires_at ? new Date(expiryInfo.min_expires_at).toLocaleString("ro-RO") : "?"}
                  {expiryInfo.critical_cookie_name && (
                    <span className="ml-1 text-zinc-500">
                      (cookie ancoră: <code>{expiryInfo.critical_cookie_name}</code>)
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-zinc-500">
                  Cu 3 zile înainte de expirare primești automat notificare în clopoțel + email cu instrucțiuni. Rulează la fiecare 6h.
                </div>
                {expiryInfo.transient_cookies > 0 && (
                  <div className="mt-1 text-zinc-500">
                    Ignor {expiryInfo.transient_cookies} cookies tranziente (&lt;24h — token-uri CSRF/session care se regenerează automat la fiecare cerere; NU țin sesiunea).
                  </div>
                )}
                {expiryInfo.all_expiries && expiryInfo.all_expiries.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-zinc-400 hover:text-zinc-200">
                      Vezi toate cookie-urile ({expiryInfo.all_expiries.length})
                    </summary>
                    <div className="mt-1 rounded bg-zinc-950/60 p-2 font-mono text-[9px] leading-relaxed">
                      {expiryInfo.all_expiries.map((c) => {
                        const dt = new Date(c.expires * 1000);
                        const hours = (c.expires * 1000 - Date.now()) / 3600000;
                        const isTransient = hours < 24;
                        return (
                          <div key={c.name} className={isTransient ? "text-zinc-500 line-through" : "text-zinc-300"}>
                            {isTransient ? "⏭️" : "🔑"} <strong>{c.name}</strong> → {dt.toLocaleString("ro-RO")} ({hours < 24 ? `${hours.toFixed(1)}h` : `${(hours/24).toFixed(1)}d`})
                          </div>
                        );
                      })}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>

          {running && runStatus && (
            <div data-testid="rpa-running-banner" className={`rounded-md border px-3 py-2 text-xs ${
              runStatus.is_stale
                ? "border-red-500/40 bg-red-500/10 text-red-200"
                : "border-orange-500/30 bg-orange-500/10 text-orange-100"
            }`}>
              <div className="flex items-center gap-2">
                <span className={`inline-block h-2 w-2 rounded-full ${runStatus.is_stale ? "bg-red-500" : "bg-emerald-500 animate-pulse"}`}></span>
                <strong>Rulare în curs</strong>
                {runStatus.family_id && <span className="font-mono">· familia {runStatus.family_id.slice(0,8)}</span>}
                {runStatus.started_at && (
                  <span className="ml-auto text-[10px] opacity-70">
                    pornit {new Date(runStatus.started_at).toLocaleTimeString("ro-RO")}
                  </span>
                )}
              </div>
              <div className="mt-1 text-[10px] opacity-90">
                {runStatus.is_stale ? (
                  <>⚠️ Fără heartbeat de {Math.round((runStatus.heartbeat_age_seconds || 0)/60)} min — pare blocată. Apasă „Anulare rulare&rdquo;.</>
                ) : (
                  <>Se rulează în background (chiar dacă ieși din pagină). Ultima activitate acum {runStatus.heartbeat_age_seconds || 0}s.</>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              data-testid="rpa-save-btn"
              onClick={save}
              disabled={saving || !email || !password}
              className="rounded border border-emerald-500/40 bg-emerald-500/10 px-4 py-1.5 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50">
              {saving ? "Salvez..." : "Salvează credențiale"}
            </button>
            <button
              data-testid="rpa-test-btn"
              onClick={testConnection}
              disabled={testing || !status?.configured}
              className="rounded border border-blue-500/40 bg-blue-500/10 px-4 py-1.5 text-xs font-semibold text-blue-200 hover:bg-blue-500/20 disabled:opacity-50">
              {testing ? "Testez..." : "Test conexiune (dry-run login)"}
            </button>
            <button
              data-testid="rpa-run-btn"
              onClick={runNow}
              disabled={running || !status?.configured}
              className="rounded border border-orange-500/60 bg-orange-500/15 px-4 py-1.5 text-xs font-semibold text-orange-100 hover:bg-orange-500/25 disabled:opacity-50">
              {running ? "Rulează..." : (selectedFamilyId ? "Rulează pentru familia aleasă" : "Rulează ACUM (toate familiile)")}
            </button>
            {running && (
              <button
                data-testid="rpa-cancel-run-btn"
                onClick={resetStuckRun}
                title="Anulează rularea RPA curentă (deblochează UI-ul dacă e blocată)."
                className="rounded border border-red-500/60 bg-red-500/15 px-4 py-1.5 text-xs font-semibold text-red-100 hover:bg-red-500/25">
                ⏹ Anulare rulare
              </button>
            )}
            <select
              data-testid="rpa-family-selector"
              value={selectedFamilyId}
              onChange={(e) => setSelectedFamilyId(e.target.value)}
              disabled={running}
              className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-200 hover:border-orange-500/40 focus:border-orange-500/60 focus:outline-none disabled:opacity-50"
              title="Alege o familie pentru rulare controlată — util pentru probă înainte de a rula pe toate.">
              <option value="">— toate familiile —</option>
              {families.map(f => (
                <option key={f.id} value={f.id}>
                  {f.name} ({f.unreplied_positive_count} fără reply)
                </option>
              ))}
            </select>
            {selectedFamilyId && (() => {
              const fam = families.find(f => f.id === selectedFamilyId);
              if (!fam?.review_url) return null;
              return (
                <a
                  data-testid="rpa-family-emag-link"
                  href={fam.review_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-orange-500/40"
                  title="Deschide pagina de review-uri pe eMAG pentru familia aleasă (verifică rezultatul a doua zi).">
                  Vezi pe eMAG ↗
                </a>
              );
            })()}
            <button
              data-testid="rpa-test-expiry-alert-btn"
              onClick={testExpiryAlert}
              disabled={testingAlert || !status?.cookies_configured}
              title="Trimite manual notificarea de expirare cookies (clopoțel + email) — pentru verificarea fluxului."
              className="rounded border border-amber-500/40 bg-amber-500/10 px-4 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-500/20 disabled:opacity-50">
              {testingAlert ? "Trimit..." : "Test alertă expirare"}
            </button>
            <button
              data-testid="rpa-reset-stuck-btn"
              onClick={resetStuckRun}
              title="Resetează starea unei rulări blocate (când primești eroarea 'Deja rulează o sesiune RPA')."
              className="rounded border border-zinc-700 bg-zinc-900 px-4 py-1.5 text-xs font-semibold text-zinc-300 hover:border-red-500/40 hover:text-red-300">
              🔧 Reset run blocat
            </button>
            <button
              data-testid="rpa-download-local-agent-btn"
              onClick={downloadLocalAgent}
              title="Descarcă script-ul Mac care rulează RPA local pe PC-ul tău — bypass total la Amazon WAF."
              className="rounded border border-blue-500/50 bg-blue-500/15 px-4 py-1.5 text-xs font-semibold text-blue-200 hover:bg-blue-500/25">
              💻 Descarcă script Mac
            </button>
            <button
              data-testid="rpa-rebuild-stats-btn"
              onClick={rebuildReviewStats}
              title="Reconstruiește statisticile Recenzii din DB dacă pagina Recenzii apare goală după un sync eșuat."
              className="rounded border border-purple-500/40 bg-purple-500/10 px-4 py-1.5 text-xs font-semibold text-purple-200 hover:bg-purple-500/20">
              🔄 Reconstruiește stats Recenzii
            </button>
          </div>

          {lastResult && (
            <div className="mt-3 rounded border border-zinc-800 bg-zinc-900/60 p-3">
              <div className="mb-1 flex items-center justify-between">
                <div className="text-[10px] font-semibold uppercase text-zinc-500">Ultimul rezultat</div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="rpa-send-last-result-email-btn"
                    onClick={sendLastResultEmail}
                    className="rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-200 hover:bg-emerald-500/20">
                    Trimite raportul pe email
                  </button>
                  {lastResult?.debug_screenshot_saved && (
                    <a
                      href={`${API}/emag-vendor-rpa/debug-screenshot`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] font-semibold text-orange-300 underline hover:text-orange-200">
                      Descarcă screenshot debug
                    </a>
                  )}
                </div>
              </div>
              <pre className="whitespace-pre-wrap text-[11px] text-zinc-300">{JSON.stringify(lastResult, null, 2)}</pre>
              {/* Per-family 0-cards diagnostic downloads */}
              {lastResult?.per_family && Object.entries(lastResult.per_family).map(([famName, famData]) => {
                if (!famData?.diagnostics || famData.cards_seen !== 0) return null;
                const diag = famData.diagnostics;
                const slug = famName.replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40);
                return (
                  <div key={famName} className="mt-3 rounded border border-amber-500/40 bg-amber-500/10 p-3 text-[11px] text-amber-100">
                    <div className="mb-1 font-semibold">
                      🔍 Diagnostic pentru "{famName}" — bot-ul a găsit 0 review cards pe pagină
                    </div>
                    <div className="space-y-0.5 text-amber-200/90">
                      <div><strong>URL vizitat:</strong> <code className="text-[10px]">{diag.visited_url}</code></div>
                      {diag.final_url && diag.final_url !== diag.visited_url && (
                        <div><strong>URL final (după redirect):</strong> <code className="text-[10px]">{diag.final_url}</code></div>
                      )}
                      {diag.page_title && <div><strong>Titlu pagină:</strong> {diag.page_title}</div>}
                      {diag.redirected_to_auth && (
                        <div className="mt-1 rounded bg-red-500/20 px-2 py-1 text-red-200">
                          ⚠️ <strong>Redirect la auth.emag.ro</strong> — cookies-urile NU logează bot-ul. Trebuie exportate din nou.
                        </div>
                      )}
                      {typeof diag.login_link_visible === "number" && diag.login_link_visible > 0 && (
                        <div className="mt-1 rounded bg-red-500/20 px-2 py-1 text-red-200">
                          ⚠️ Pagina afișează link „Autentificare/Contul meu" ({diag.login_link_visible}) — bot-ul e văzut ca vizitator, nu ca vendor logat.
                        </div>
                      )}
                      {typeof diag.adauga_button_count_global === "number" && (
                        <div><strong>Butoane "Adauga comentariu" globale găsite:</strong> {diag.adauga_button_count_global}</div>
                      )}
                      {diag.body_text_sample && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-amber-200 hover:text-amber-100">Vezi primele 800 caractere de text de pe pagină</summary>
                          <pre className="mt-1 max-h-40 overflow-auto rounded bg-zinc-950/60 p-2 text-[10px] text-zinc-300 whitespace-pre-wrap">{diag.body_text_sample}</pre>
                        </details>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {diag.screenshot_path && (
                        <button
                          data-testid={`rpa-diag-screenshot-${slug}`}
                          onClick={() => downloadDebugFile("debug-screenshot", { name: `no_cards_${slug}` }, `rpa_no_cards_${slug}.png`)}
                          className="rounded border border-amber-500/60 bg-amber-500/20 px-3 py-1 text-[11px] font-semibold text-amber-100 hover:bg-amber-500/30">
                          📸 Descarcă screenshot ({famName})
                        </button>
                      )}
                      {diag.html_path && (
                        <button
                          data-testid={`rpa-diag-html-${slug}`}
                          onClick={() => downloadDebugFile("debug-html", { name: slug }, `rpa_no_cards_${slug}.html`)}
                          className="rounded border border-amber-500/60 bg-amber-500/20 px-3 py-1 text-[11px] font-semibold text-amber-100 hover:bg-amber-500/30">
                          📄 Descarcă HTML pagină
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- IP diagnostic (kept from before) ----------
function IPDiagnosticCard() {
  const [ip, setIp] = useState(null);
  const [emagTest, setEmagTest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const check = async () => {
    setLoading(true); setEmagTest(null);
    try {
      const [ipRes, testRes] = await Promise.all([
        axios.get(`${API}/config/outbound-ip`),
        axios.get(`${API}/config/emag-test`),
      ]);
      setIp(ipRes.data.outbound_ip);
      setEmagTest(testRes.data);
    } finally { setLoading(false); }
  };
  const scan = async () => {
    setScanning(true); setScanResult(null);
    try {
      const r = await axios.get(`${API}/config/scan-ips`, { params: { samples: 30 } });
      setScanResult(r.data);
    } finally { setScanning(false); }
  };
  // eslint-disable-next-line
  useEffect(() => { check(); }, []);
  const copyText = (text, key) => { navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 2000); };

  const ok = emagTest?.ok === true;
  return (
    <div data-testid="ip-diagnostic" className={`mb-8 rounded-2xl border p-5 ${ok ? "border-emerald-500/40 bg-emerald-500/5" : ip ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-950/60"}`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1">
          <div className="mb-2 flex items-center gap-2"><Globe className="h-4 w-4 text-zinc-400" /><h3 className="text-sm font-semibold text-zinc-100">IP de outbound pentru eMAG</h3></div>
          <p className="text-xs text-zinc-500">IP-ul prin care aplicația vorbește cu eMAG. Trebuie să fie whitelisted în contul eMAG.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="rounded-lg border border-zinc-800 bg-black/40 px-3 py-2 font-mono text-base text-zinc-100">{loading ? "Detectare..." : ip || "necunoscut"}</code>
            {ip && <button onClick={() => copyText(ip, "single")} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-200 hover:border-orange-500/40"><Copy className="h-3.5 w-3.5" />{copied === "single" ? "Copiat!" : "Copiază"}</button>}
            <button onClick={check} disabled={loading} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-200"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />Verifică</button>
            <button onClick={scan} disabled={scanning} className="inline-flex items-center gap-1.5 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-xs text-orange-200"><Radar className={`h-3.5 w-3.5 ${scanning ? "animate-spin" : ""}`} />{scanning ? "Scanare..." : "Scanează pool"}</button>
          </div>
          {emagTest && (
            <div className={`mt-3 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs ${ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-rose-500/40 bg-rose-500/10 text-rose-200"}`}>
              {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
              {ok ? "Conexiune eMAG OK" : `eMAG: ${emagTest.error}`}
            </div>
          )}
          {scanResult && (
            <div className="mt-4 rounded-xl border border-zinc-800 bg-black/40 p-3">
              <p className="mb-2 text-xs text-zinc-300">Pool: {scanResult.unique_ips} IP-uri unice ({scanResult.samples} teste)</p>
              {scanResult.ips.map((entry) => (
                <div key={entry.ip} className="flex items-center gap-2 py-1">
                  <code className="flex-1 text-xs text-zinc-100">{entry.ip}</code>
                  <span className="text-[10px] text-zinc-500">{entry.percentage}%</span>
                  <button onClick={() => copyText(entry.ip, entry.ip)} className="rounded border border-zinc-800 bg-zinc-900 px-2 py-0.5 text-[10px] text-zinc-300">{copied === entry.ip ? "✓" : "Copy"}</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Edit Integration Modal ----------
const FIELD_SCHEMAS = {
  emag: [
    { key: "username", label: "Username (cont seller)", type: "text", required: true },
    { key: "password", label: "Parolă API", type: "password", required: true },
    { key: "oblio_series", label: "Serie facturare Oblio (ex: BRAGFBE, BRAG)", type: "text", config: true },
    { key: "oblio_management", label: "Gestiune Oblio (ex: EMAG FBE, Depozit Cant_Val)", type: "text", config: true },
    { key: "emag_courier_locker_id", label: "Courier account_id pentru Locker (auto-detect dacă gol — ex: eMAG Courier Locker EasyBox)", type: "text", config: true },
    { key: "emag_courier_home_id", label: "Courier account_id pentru Domiciliu (auto-detect dacă gol — ex: Sameday)", type: "text", config: true },
  ],
  oblio: [
    { key: "email", label: "Email Oblio (client_id)", type: "text", required: true },
    { key: "client_secret", label: "Client Secret (API token)", type: "password", required: true },
    { key: "cif", label: "CIF firmă (ex: RO12345678)", type: "text", config: true },
    { key: "workstation", label: "Punct de lucru Oblio (ex: Sediu, Terti) — default: Sediu", type: "text", config: true },
  ],
  sameday: [
    { key: "username", label: "Username Sameday", type: "text", required: true },
    { key: "password", label: "Parolă Sameday", type: "password", required: true },
    { key: "pickup_point_id", label: "Pickup Point ID Sameday (ex: 229442)", type: "text", config: true },
    { key: "default_home_service_id", label: "Service ID livrare standard (ex: 7)", type: "text", config: true },
    { key: "sandbox", label: "Sandbox (bifează pentru sandbox / mediu de test)", type: "checkbox", config: true },
    { key: "default_weight_kg", label: "Greutate colet standard (kg) — folosit la AWB-uri retur", type: "text", config: true },
    { key: "default_length_cm", label: "Lungime colet standard (cm)", type: "text", config: true },
    { key: "default_width_cm", label: "Lățime colet standard (cm)", type: "text", config: true },
    { key: "default_height_cm", label: "Înălțime colet standard (cm)", type: "text", config: true },
    { key: "awb_contact_name", label: "Persoană contact AWB retur (nume)", type: "text", config: true },
    { key: "pv_representative_name", label: "Reprezentant pentru PV Service (apare pe semnătură). Implicit „Dziuzenschi Cazimir”", type: "text", config: true },
    { key: "awb_contact_phone", label: "Telefon contact AWB retur", type: "text", config: true },
    { key: "awb_contact_email", label: "Email contact AWB retur", type: "text", config: true },
    { key: "return_address", label: "Adresă retur (unde primim coletele)", type: "text", config: true },
    { key: "return_city", label: "Oraș retur (ex: Baia Mare)", type: "text", config: true },
    { key: "return_county", label: "Județ retur (ex: Maramureș)", type: "text", config: true },
    { key: "return_postal_code", label: "Cod poștal retur", type: "text", config: true },
    { key: "exchange_delivery_service_id", label: "Service ID „NextDay 24H” pentru Colet la schimb (opțional, default 7)", type: "text", config: true },
    { key: "exchange_swap_tax_id", label: "ID taxă „Colet la schimb” Sameday (auto-detectat — completează DOAR dacă auto-detecția eșuează)", type: "text", config: true },
  ],
  gomag: [
    { key: "shop_url", label: "URL magazin (ex: bragus.ro)", type: "text", required: true },
    { key: "api_key", label: "API Key", type: "password", required: true },
    { key: "oblio_series", label: "Serie facturare Oblio (ex: BRAG, BRAGFBE)", type: "text", config: true },
    { key: "oblio_management", label: "Gestiune Oblio (ex: Depozit Cant_Val)", type: "text", config: true },
    // ID-ul statusului custom „Neridicată" din Gomag (Comenzi → Statusuri).
    // Când o comandă eMAG e stornată și AWB-ul se întoarce la depozit,
    // BragusApp face push automat la Gomag cu acest statusId.
    { key: "neridicata_status_id", label: "StatusID Gomag pentru „Neridicată” (ex: 19)", type: "text", config: true },
  ],
  shopify: [
    { key: "shop_domain", label: "Domeniu magazin Shopify (ex: bankgw-0x.myshopify.com)", type: "text", required: true },
    { key: "api_key", label: "Admin API Access Token (shpat_…)", type: "password", required: true },
    { key: "api_secret_key", label: "Admin API Secret Key (opțional)", type: "password" },
    { key: "oblio_series", label: "Serie facturare Oblio (ex: BRAG)", type: "text", config: true },
    { key: "oblio_management", label: "Gestiune Oblio (ex: Depozit Cant_Val)", type: "text", config: true },
  ],
  trendyol: [
    { key: "supplier_id", label: "ID comerciant (Supplier ID)", type: "text", required: true },
    { key: "api_key", label: "Cheie API (API Key)", type: "text", required: true },
    { key: "api_secret", label: "Secret API (API Secret)", type: "password", required: true },
    { key: "integration_ref", label: "Codul de referință al integrării (opțional)", type: "text", config: true },
    { key: "oblio_series", label: "Serie facturare Oblio (ex: BRAGTRE)", type: "text", config: true },
    { key: "oblio_management", label: "Gestiune Oblio", type: "text", config: true },
  ],
  netopia: [
    { key: "api_key", label: "API Key Netopia (Profil > Securitate)", type: "password", required: true },
    { key: "default_report_id", label: "Report ID default (ex: 3201700) — apare in URL-ul de descarcare", type: "text", config: true },
  ],
  smtp: [
    { key: "host", label: "Server SMTP (ex: mail.bragus.ro, smtp.gmail.com)", type: "text", required: true },
    { key: "port", label: "Port (465 pentru SSL, 587 pentru STARTTLS)", type: "text", required: true },
    { key: "username", label: "Adresă email completă (ex: contact@bragus.ro)", type: "text", required: true },
    { key: "password", label: "Parolă cont email (sau App Password pentru Gmail)", type: "password", required: true },
    { key: "use_ssl", label: "SSL (bifează pentru port 465; debifează pentru 587 STARTTLS)", type: "checkbox", config: true },
    { key: "from_email", label: "Email expeditor afișat destinatarului (ex: contact@bragus.ro)", type: "text", config: true },
    { key: "from_name", label: "Nume expeditor afișat (ex: Bragus)", type: "text", config: true },
  ],
};

function SmtpTestPanel({ itemKey, form, isActive }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const send = async () => {
    if (!to) { setResult({ ok: false, msg: "Introdu o adresă destinatar." }); return; }
    setBusy(true); setResult(null);
    try {
      const r = await axios.post(`${API}/integrations/${itemKey}/test`, { to });
      setResult({ ok: true, msg: `Email de test trimis către ${r.data?.to || to}.` });
    } catch (e) {
      setResult({ ok: false, msg: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-3 space-y-2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300">Test trimitere email</p>
      <p className="text-[11px] text-zinc-400 leading-relaxed">
        Trimite un email de test pentru a verifica că server-ul, portul și parola sunt corecte.
        <strong className="text-zinc-200"> Salvează întâi</strong>, apoi click pe &bdquo;Trimite test&rdquo;.
      </p>
      <div className="flex gap-2">
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="ex: tu@gmail.com"
          data-testid="smtp-test-recipient"
          className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/50" />
        <button onClick={send} disabled={busy || !isActive} data-testid="smtp-test-send"
          className="rounded-lg bg-emerald-500/20 border border-emerald-500/40 px-3 py-2 text-xs font-medium text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-50">
          {busy ? "Se trimite…" : "Trimite test"}
        </button>
      </div>
      {!isActive && <p className="text-[10px] text-amber-300">Bifează &bdquo;Activă&rdquo; și salvează înainte de a testa.</p>}
      {result && (
        <p className={`text-xs ${result.ok ? "text-emerald-300" : "text-rose-300"}`}>
          {result.ok ? "✓ " : "✕ "}{result.msg}
        </p>
      )}
    </div>
  );
}

function IntegrationEditModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [isActive, setIsActive] = useState(false);
  const [showPwd, setShowPwd] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!item) return;
    const t = setTimeout(() => {
      const initial = { ...(item.credentials_masked || {}) };
      const cfg = item.config || {};
      for (const k of Object.keys(cfg)) initial[k] = cfg[k];
      setForm(initial);
      setIsActive(!!item.is_active);
    }, 0);
    return () => clearTimeout(t);
  }, [item]);

  if (!item) return null;
  const schema = FIELD_SCHEMAS[item.type] || FIELD_SCHEMAS.emag;
  const isSmtp = item.type === "smtp";

  const save = async () => {
    setError(""); setSaving(true);
    try {
      const credsToSend = {};
      const configToSend = {};
      for (const f of schema) {
        const v = form[f.key];
        if (f.config) {
          configToSend[f.key] = v ?? "";
        } else if (v && !String(v).startsWith("•")) {
          credsToSend[f.key] = v;
        }
      }
      const body = { credentials: credsToSend, is_active: isActive };
      if (Object.keys(configToSend).length) body.config = configToSend;
      const r = await axios.put(`${API}/integrations/${item.key}`, body);
      onSaved(r.data);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-t-2xl border border-zinc-800 bg-zinc-950 shadow-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid={`edit-integration-${item.key}`}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-800 bg-zinc-950/95 px-5 py-3">
          <div className="flex items-center gap-2">
            {item.flag && <span className="text-2xl">{item.flag}</span>}
            <div>
              <h3 className="text-base font-semibold text-zinc-100">{item.name}</h3>
              <p className="text-xs text-zinc-500">{item.description || "Configurare integrare"}</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-3 p-5">
          {error && <div className="flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200"><AlertCircle className="mt-0.5 h-3.5 w-3.5" />{error}</div>}

          {schema.map((f) => (
            <div key={f.key}>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">{f.label}{f.required && " *"}</label>
              {f.type === "checkbox" ? (
                <label className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 cursor-pointer">
                  <input data-testid={`integration-field-${f.key}`} type="checkbox"
                    checked={Boolean(form[f.key]) && String(form[f.key]).toLowerCase() !== "false"}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                    className="accent-orange-500" />
                  <span className="text-sm text-zinc-200">{form[f.key] && String(form[f.key]).toLowerCase() !== "false" ? "Activat" : "Dezactivat"}</span>
                </label>
              ) : (
                <div className="relative">
                  <input type={f.type === "password" && !showPwd[f.key] ? "password" : "text"}
                    value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50 pr-10" />
                  {f.type === "password" && (
                    <button type="button" onClick={() => setShowPwd((p) => ({ ...p, [f.key]: !p[f.key] }))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200">
                      {showPwd[f.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  )}
                </div>
              )}
              {form[f.key] && String(form[f.key]).startsWith("•") && (
                <p className="mt-1 text-[10px] text-zinc-600">Lasă bullets pentru a păstra parola existentă, sau introdu una nouă.</p>
              )}
            </div>
          ))}

          <label className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="accent-orange-500" />
            <span className="text-sm text-zinc-200">Activă (folosește pentru sync)</span>
          </label>

          {isSmtp && (
            <SmtpTestPanel itemKey={item.key} form={form} isActive={isActive} />
          )}

          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 py-2 text-sm text-zinc-300">Anulează</button>
            <button onClick={save} disabled={saving} data-testid="integration-save-btn"
              className="flex-1 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 py-2 text-sm font-medium text-white disabled:opacity-50">{saving ? "Se salvează..." : "Salvează"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- eMAG Sender (Date expeditor) Modal ----------
function EmagSenderModal({ accountKey, onClose }) {
  const [form, setForm] = useState({
    name: "Pickup ONB RO37343423",
    contact: "Mircea-Cazimir Dziuzenschi",
    phone1: "0750433328",
    phone2: "",
    legal_entity: 1,
    locality_id: 26,
    street: "Strada Rezervelor 58C",
    zipcode: "077040",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!accountKey) return;
    setLoading(true);
    axios.get(`${API}/emag/${accountKey}/sender`)
      .then((r) => {
        const cur = r.data?.sender;
        if (cur && cur.name) setForm({ phone2: "", ...cur });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [accountKey]);

  const save = async () => {
    setErr(""); setOk(false);
    if (!form.name || !form.contact || !form.phone1 || !form.street || !form.locality_id) {
      setErr("Toate câmpurile marcate sunt obligatorii.");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/emag/${accountKey}/sender`, {
        ...form,
        legal_entity: Number(form.legal_entity) || 1,
        locality_id: Number(form.locality_id),
      });
      setOk(true);
      setTimeout(onClose, 800);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!accountKey) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="emag-sender-modal">
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{accountKey}</div>
            <h3 className="text-sm font-semibold text-zinc-100">Date expeditor AWB eMAG</h3>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-zinc-500">
            Aceste date sunt trimise în payload-ul AWB către eMAG ca expeditor. Sunt valori pre-completate cu pickup point-ul tău Sameday existent. Verifică-le și apasă &quot;Salvează&quot;.
          </p>
          {loading ? (
            <div className="text-center text-xs text-zinc-500 py-4">Se încarcă...</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Nume firmă/pickup *</label>
                  <input data-testid="sender-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                    className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Persoană contact *</label>
                  <input data-testid="sender-contact" value={form.contact} onChange={(e) => setForm({...form, contact: e.target.value})}
                    className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Telefon principal *</label>
                  <input data-testid="sender-phone1" value={form.phone1} onChange={(e) => setForm({...form, phone1: e.target.value})}
                    placeholder="0750433328"
                    className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Telefon secundar</label>
                  <input data-testid="sender-phone2" value={form.phone2 || ""} onChange={(e) => setForm({...form, phone2: e.target.value})}
                    className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Adresă completă *</label>
                  <input data-testid="sender-street" value={form.street} onChange={(e) => setForm({...form, street: e.target.value})}
                    className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Locality ID eMAG *</label>
                  <input data-testid="sender-locality" type="number" value={form.locality_id} onChange={(e) => setForm({...form, locality_id: e.target.value})}
                    className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
                  <p className="mt-1 text-[10px] text-zinc-600">ID-ul localității în baza eMAG (26 = Chiajna)</p>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Cod poștal</label>
                  <input data-testid="sender-zip" value={form.zipcode || ""} onChange={(e) => setForm({...form, zipcode: e.target.value})}
                    className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
                </div>
              </div>
              {err && <div className="rounded border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{err}</div>}
              {ok && <div className="rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">Salvat cu succes!</div>}
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-5 py-3">
          <button onClick={onClose} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-700">Anulează</button>
          <button data-testid="sender-save" onClick={save} disabled={saving || loading}
            className="inline-flex items-center gap-1.5 rounded-md bg-orange-500 px-4 py-1.5 text-xs font-medium text-white hover:bg-orange-400 disabled:opacity-60">
            {saving ? "Se salvează..." : "Salvează"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------- Logo / brand box ----------
function IntegrationLogo({ item }) {
  // For eMAG: orange "eMAG" text on black bg + flag
  if (item.logo === "emag") {
    return (
      <div className="flex h-12 w-12 flex-col items-center justify-center rounded-xl border border-orange-500/30 bg-gradient-to-br from-orange-500/15 to-rose-500/5">
        <span className="text-[11px] font-extrabold tracking-tight text-orange-300">eMAG</span>
        {item.flag && <span className="text-sm leading-none">{item.flag}</span>}
      </div>
    );
  }
  // Real logo image for Shopify (green shopping bag).
  if (item.logo === "shopify") {
    return (
      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/15 to-teal-500/5 p-1.5">
        <img src="/shopify-logo.svg" alt="Shopify" className="h-full w-full object-contain" />
      </div>
    );
  }
  const colors = { gomag: "from-sky-500/15 to-blue-500/5 border-sky-500/30 text-sky-300", oblio: "from-emerald-500/15 to-teal-500/5 border-emerald-500/30 text-emerald-300", sameday: "from-amber-500/15 to-orange-500/5 border-amber-500/30 text-amber-300" };
  const cls = colors[item.logo] || "from-zinc-800 to-zinc-900 border-zinc-700 text-zinc-200";
  return (
    <div className={`flex h-12 w-12 items-center justify-center rounded-xl border bg-gradient-to-br text-xs font-bold ${cls}`}>
      {item.name.slice(0, 2).toUpperCase()}
    </div>
  );
}

// ---------- Main page ----------
export default function Integrations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [senderAccountKey, setSenderAccountKey] = useState(null);

  const load = async () => {
    setLoading(true); setLoadError(null);
    try { const r = await axios.get(`${API}/integrations`); setData(r.data); }
    catch (e) { setLoadError(e.response?.data?.detail || e.message || "Necunoscut"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  if (loading) return <div className="px-8 py-12 text-zinc-500">Se încarcă...</div>;
  if (!data) return (
    <div className="px-4 py-6 sm:px-6 lg:px-8" data-testid="integrations-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Integrări</h1>
      </div>
      <div className="mb-6 rounded-lg border border-red-500/40 bg-red-500/10 p-4">
        <p className="text-sm text-red-200">Eroare la încărcarea integrărilor: <code className="text-xs">{loadError || "răspuns gol"}</code></p>
        <button onClick={load} className="mt-2 rounded border border-red-500/40 bg-red-500/20 px-3 py-1 text-xs text-red-100">Reîncarcă</button>
      </div>
      <IPDiagnosticCard />
      <EmagVendorRPACard />
    </div>
  );

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8" data-testid="integrations-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Integrări</h1>
        <p className="text-sm text-zinc-500">Marketplace-uri, magazin online, facturare și curieri</p>
      </div>

      <IPDiagnosticCard />
      <EmagVendorRPACard />

      <div className="space-y-10">
        {data.categories.map((cat) => {
          const Icon = CATEGORY_ICONS[cat.key] || ShoppingBag;
          return (
            <section key={cat.key} data-testid={`category-${cat.key}`}>
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-xl bg-zinc-900 p-2 text-zinc-300"><Icon className="h-4 w-4" /></div>
                <h2 className="text-lg font-semibold text-zinc-100">{cat.label}</h2>
                <span className="text-xs text-zinc-500">{cat.items.length}</span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {cat.items.map((item) => {
                  const connected = item.is_active && item.has_credentials;
                  return (
                    <div key={item.key} data-testid={`integration-${item.key}`}
                      className={`rounded-2xl border bg-zinc-950/70 p-5 transition hover:border-zinc-700 ${connected ? "border-emerald-500/30" : "border-zinc-800"}`}>
                      <div className="mb-3 flex items-start justify-between">
                        <IntegrationLogo item={item} />
                        {connected ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
                            <CheckCircle2 className="h-3 w-3" /> Conectat
                          </span>
                        ) : item.has_credentials ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-300">
                            Inactiv
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-[10px] font-medium text-zinc-400">
                            Neconfigurat
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-semibold text-zinc-100">{item.name}</h3>
                      <p className="mt-1 text-xs text-zinc-500">{item.description || (item.platform ? `eMAG ${item.platform.toUpperCase()} · ${item.account_type?.toUpperCase()}` : " ")}</p>
                      <button onClick={() => setEditItem(item)}
                        data-testid={`configure-${item.key}`}
                        className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 py-1.5 text-xs font-medium text-zinc-200 hover:border-orange-500/40 hover:bg-orange-500/10 hover:text-orange-200">
                        <Settings className="h-3.5 w-3.5" /> {item.has_credentials ? "Editează" : "Configurează"}
                      </button>
                      {item.logo === "emag" && item.account_type === "bau" && (
                        <button onClick={() => setSenderAccountKey(item.key)}
                          data-testid={`sender-${item.key}`}
                          className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/5 py-1.5 text-xs font-medium text-amber-200 hover:border-amber-500/50 hover:bg-amber-500/10">
                          <Send className="h-3.5 w-3.5" /> Date expeditor AWB
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <IntegrationEditModal item={editItem} onClose={() => setEditItem(null)} onSaved={() => load()} />
      <EmagSenderModal accountKey={senderAccountKey} onClose={() => setSenderAccountKey(null)} />
    </div>
  );
}
